import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, ShieldCheck, TrendingDown, CalendarClock } from "lucide-react";
import { useDemoState } from "../demo/DemoStateProvider";
import { SettingsSnapshot } from "../demo/SettingsSnapshot";
import type { DemoSession } from "../demo/mock-data";

/**
 * DemoClientProfile (/client/:id) — a single client's profile: identity, saved
 * preferences (which auto-apply when you start a session for them), and their
 * session history with the full Client → Session → Theme → Topic → Emotion
 * structure. All fixtures; nothing is fetched or stored.
 */
export function DemoClientProfile() {
  const { id = "" } = useParams();
  const { clientById, sessionsFor } = useDemoState();
  const client = clientById(id);
  const sessions = sessionsFor(id);

  if (!client) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="font-heading text-2xl text-[var(--color-text-primary)]">Client not found</p>
        <Link to="/dashboard" className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-gold-soft)]">
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
      </main>
    );
  }

  const age = ageFromDob(client.dob);

  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)]">
        <ArrowLeft className="h-3 w-3" /> Dashboard
      </Link>

      {/* Identity header */}
      <header className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium text-[var(--color-text-primary)]">{client.displayName}</h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">
            <span>{age} yrs</span><span aria-hidden>·</span>
            <span>{client.state}</span><span aria-hidden>·</span>
            <span>{client.modality}</span><span aria-hidden>·</span>
            <span className={client.consentStatus === "signed" ? "inline-flex items-center gap-1 text-[var(--color-gold-soft)]" : "inline-flex items-center gap-1 text-[var(--color-text-muted)]"}>
              <ShieldCheck className="h-3 w-3" /> Consent {client.consentStatus}
            </span>
          </p>
        </div>
        <Link
          to={`/session?client=${client.id}`}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-5 py-2.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
        >
          <Play className="h-4 w-4" /> Start session
        </Link>
      </header>

      {/* Saved preferences */}
      <section className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]">Saved preferences</h2>
        <p className="mt-0.5 text-[12px] text-[var(--color-text-ghost)]">These apply automatically when you start a session for {client.displayName.split(" ")[0]}.</p>
        <SettingsSnapshot settings={client.savedPrefs} />
      </section>

      {/* Session history */}
      <section className="mt-6">
        <h2 className="mb-3 font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]">
          Session history ({sessions.length})
        </h2>
        {sessions.length === 0 ? (
          <p className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-bg-deep)] px-5 py-8 text-center text-[13px] text-[var(--color-text-ghost)]">
            No sessions yet. Start one above — it&apos;ll appear here under {client.displayName.split(" ")[0]}.
          </p>
        ) : (
          <ul className="space-y-3">
            {sessions.map((s) => (
              <SessionRow key={s.id} session={s} clientId={client.id} />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

/** A session row showing the full hierarchy — never a vague "Session completed" card. */
function SessionRow({ session: s, clientId }: { session: DemoSession; clientId: string }) {
  // Only a real pre AND post reading yields a distress-change badge — a skipped
  // (null) scale must neither fabricate an improvement nor suppress a real one.
  const delta = s.pre.distress != null && s.post.distress != null ? s.pre.distress - s.post.distress : 0;
  return (
    <li>
      <Link
        to={`/client/${clientId}/session/${s.id}`}
        className="block rounded-xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] px-5 py-4 transition-colors hover:border-[var(--color-border-gold)]"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-gold-soft)]">
            {s.theme} · {s.modality}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">{s.date} · {s.durationMin} min</span>
        </div>
        <p className="mt-1 font-heading text-[17px] font-medium text-[var(--color-text-primary)]">{s.topic}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="rounded-full border border-[var(--color-border)] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
            Target: {s.targetEmotion}
          </span>
          {delta > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border-gold)] px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-gold-soft)]">
              <TrendingDown className="h-3 w-3" /> Distress {s.pre.distress} → {s.post.distress}
            </span>
          )}
          {s.followUp && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)]">
              <CalendarClock className="h-3 w-3" /> Follow-up {s.followUp.dueDate}
            </span>
          )}
        </div>
      </Link>
    </li>
  );
}

function ageFromDob(dob: string): number {
  const b = new Date(dob);
  const now = new Date(2026, 5, 22); // demo "today" — fixed so fixtures are stable
  let a = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) a--;
  return a;
}
